import { FormState, FieldState } from 'formstate';
import { Location } from 'history';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';

import { AuthenticateRequest } from 'src/api';
import { BusyFormState } from 'src/util/busy-form';
import { emailValidator } from 'src/util/validators';
import { LoginStore } from 'src/login/stores/login.store';
import { lazyInject } from '../../ioc';

import { LoginPage } from './desktop/login-page';

interface LocationState {
    from?: Location;
}

class LoginFormState extends BusyFormState {
    email = new FieldState('').validators(emailValidator);
    password = new FieldState('').validators((value) =>
        value && value.length >= 8 ? null : 'error'
    );
    stayLoggedIn = new FieldState(true);

    form = new FormState({
        email: this.email,
        password: this.password,
        stayLoggedIn: this.stayLoggedIn,
        isBusy: this.isBusy,
    });

    constructor() {
        super();
        this.form.validate();
    }
}

@observer
export class LoginPageContainer extends React.Component<RouteComponentProps<void>> {
    @lazyInject(LoginStore) loginStore!: LoginStore;

    @observable loginError: string | undefined;
    data = new LoginFormState();

    componentDidMount() {
        renderReCaptcha(this.handleLogin, () => {
            this.loginError = 'An error occurred with reCAPTCHA. Please try again later.';
            this.data.setBusy(false);
        });
    }

    render() {
        return <LoginPage container={this} />;
    }

    handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (this.data.form.error) {
            return;
        }

        this.loginError = undefined;
        this.data.setBusy(true);
        grecaptcha.execute();
    };

    /**
     * @param {string} token the response token get from reCAPTCHA after user submit the form, it's used to check if the user has been verified by Google
     */
    @action private handleLogin = (token: string) => {
        const req: AuthenticateRequest = {
            email: this.data.email.$,
            password: this.data.password.$,
            reCaptchaToken: token,
            stayLoggedIn: this.data.stayLoggedIn.$,
        };
        grecaptcha.reset(); // prepare reCAPTCHA for next use in case there was an authentication error
        this.data.withBusy(this.loginStore.authenticate(req)).then(
            () => {
                const from = this.props.location.state
                    ? (this.props.location.state as LocationState).from
                    : undefined;
                this.props.history.replace(from ? from.pathname : '/');
            },
            (err) => (this.loginError = err)
        );
    };

    @action clearError = () => {
        this.loginError = undefined;
    };
}
